import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  Bot,
  Home,
  Layers,
  LayoutGrid,
  ListChecks,
  Monitor,
  Package,
  Server,
  type LucideIcon,
} from 'lucide-react';
import { useCan } from '../../hooks/useCan';
import { useClusterStore } from '../../stores/clusterStore';
import { cn } from '../../lib/utils';
import { isOpsEnabled } from '../../lib/keycloak';
import { listOpsProposals } from '../../lib/opsApi';
import { AgentHealthFooter } from './AgentHealthFooter';

export interface InspectorRailProps {
  open: boolean;
}

interface RailLink {
  to: string;
  icon: LucideIcon;
  label: string;
  badge?: number;
}

const OVERVIEW_ITEM: RailLink = { to: '/overview', icon: Home, label: 'Overview' };

const CLUSTER_ITEMS: RailLink[] = [
  { to: '/services', icon: LayoutGrid, label: 'Serviços' },
  { to: '/nodes', icon: Server, label: 'Nodes' },
];

const TASKS_ITEM: RailLink = {
  to: '/tasks',
  icon: ListChecks,
  label: 'Tarefas',
};

const STATUS_ITEM: RailLink = { to: '/status', icon: Activity, label: 'Status' };

const MOSAIC_ITEM: RailLink = { to: '/mosaic', icon: Monitor, label: 'Mosaic' };

const PACKAGES_ITEM: RailLink = { to: '/packages', icon: Package, label: 'Pacotes' };

export function InspectorRail({ open }: InspectorRailProps) {
  const canMosaic = useCan('mosaic', 'view');
  const canTasks = useCan('audit', 'view');
  const canPackages = useCan('packages', 'view');
  const canOps = useCan('ops', 'view') && isOpsEnabled();
  const isAll = useClusterStore(s => s.activeSlug) === 'all';

  const pendingProposalsQuery = useQuery({
    queryKey: ['ops-proposals', 'pending'],
    queryFn: () => listOpsProposals('pending'),
    enabled: canOps,
    refetchInterval: 30_000,
  });
  const pendingCount = pendingProposalsQuery.data?.length ?? 0;
  const opsItem: RailLink = {
    to: '/ops',
    icon: Bot,
    label: 'Operação',
    badge: pendingCount > 0 ? pendingCount : undefined,
  };

  return (
    <nav
      aria-label="Navegação principal"
      className={cn(
        'flex w-[178px] flex-shrink-0 flex-col border-r border-border bg-surface px-2 py-2.5',
        'fixed inset-y-0 left-0 z-50 transition-transform duration-200 motion-reduce:transition-none',
        'md:static md:z-auto md:translate-x-0',
        open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      )}
    >
      <div className="mb-2 flex items-center gap-2 px-1">
        <div
          aria-hidden
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-accent to-primary text-white shadow"
        >
          <Layers size={15} />
        </div>
        <span className="text-[13px] font-bold tracking-tight text-text">
          Loy Flow
        </span>
      </div>

      <RailLinkItem {...OVERVIEW_ITEM} />
      {!isAll &&
        CLUSTER_ITEMS.map(item => <RailLinkItem key={item.to} {...item} />)}
      {canTasks && <RailLinkItem {...TASKS_ITEM} />}
      {canOps && <RailLinkItem {...opsItem} />}
      {isAll && <RailLinkItem {...STATUS_ITEM} />}
      {!isAll && canMosaic && <RailLinkItem {...MOSAIC_ITEM} />}
      {!isAll && canPackages && <RailLinkItem key="/packages" {...PACKAGES_ITEM} />}

      <AgentHealthFooter />
    </nav>
  );
}

function RailLinkItem({ to, icon: Icon, label, badge }: RailLink) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          'relative flex h-[32px] items-center gap-2.5 rounded-lg px-2.5 transition-colors',
          isActive
            ? 'bg-accentSoft text-accent'
            : 'text-textMute hover:bg-surfaceAlt hover:text-text',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              aria-hidden
              className="absolute -left-px bottom-2 top-2 w-[2.5px] rounded-r bg-accent"
            />
          )}
          <Icon size={16} className="flex-shrink-0" />
          <span className="text-[12.5px] font-medium">{label}</span>
          {badge != null && (
            <span className="ml-auto min-w-[14px] rounded-[3px] bg-text px-1 py-px text-center font-mono text-[9px] font-bold leading-tight text-white">
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}
